import { useState } from "react";
import { useCart, cartStore } from "@/lib/cart";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Trash2, ArrowRight, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang, useT, uomLabel, UOM_TA } from "@/lib/i18n";

export default function CheckoutPage() {
  const { items, remove, total } = useCart();
  const [form, setForm] = useState({
    customer_name: "",
    mobile: "",
    email: "",
    address: "",
  });
  const [busy, setBusy] = useState(false);
  const { lang } = useLang();
  const t = useT();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const localName = (i) => (lang === "ta" && i.name_ta) ? i.name_ta : i.name;
  const localUom = (uom) => uomLabel(uom, lang);

  const submit = async () => {
    if (items.length === 0) return toast.error(t("empty_cart"));
    if (!form.mobile || !form.email || !form.address)
      return toast.error(`${t("mobile_number")} · ${t("email")} · ${t("delivery_address")}`);
    setBusy(true);
    try {
      const payload = {
        customer_name: form.customer_name,
        mobile: form.mobile,
        email: form.email,
        address: form.address,
        origin_url: window.location.origin,
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity_text: i.quantity_text,
          quantity_value: Number(i.quantity_value),
          unit_price: Number(i.unit_price),
          line_total: Number(i.line_total),
          name: i.name,
          uom: i.uom,
        })),
      };
      const res = await api.post("/checkout", payload);
      if (res.data?.order_id) {
        const orderId = res.data.order_id;
        cartStore.clear();
        window.location.href = `/payment/success?order_id=${orderId}`;
      } else {
        toast.error("Unable to place order");
      }
    } catch (e) {
      toast.error(errMsg(e, "Checkout failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <h1 className="font-serif text-4xl md:text-5xl text-forest mb-10 font-bold">{t("checkout")}</h1>

        {items.length === 0 ? (
          <div className="bg-white border border-black/5 rounded-2xl p-14 text-center" data-testid="empty-cart">
            <ShoppingBag className="w-10 h-10 mx-auto text-charcoal/30" />
            <p className="mt-4 text-charcoal/70">{t("empty_cart")}</p>
            <Link
              to="/"
              className="inline-block mt-6 px-5 py-2.5 rounded-full text-bone"
              style={{ backgroundColor: "var(--forest)", color: "#F9F8F6" }}
              data-testid="continue-shopping-btn"
            >
              {t("continue_shopping")}
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_420px] gap-10">
            <div className="bg-white rounded-2xl border border-black/5 p-8">
              <h2 className="font-serif text-2xl text-forest mb-6 font-bold">{t("delivery_details")}</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <Label htmlFor="name">{t("full_name")}</Label>
                  <Input id="name" data-testid="checkout-name" value={form.customer_name} onChange={update("customer_name")} />
                </div>
                <div>
                  <Label htmlFor="mobile">{t("mobile_number")}</Label>
                  <Input id="mobile" data-testid="checkout-mobile" value={form.mobile} onChange={update("mobile")} placeholder="+91 90000 00000" />
                </div>
                <div>
                  <Label htmlFor="email">{t("email")}</Label>
                  <Input id="email" data-testid="checkout-email" type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="address">{t("delivery_address")}</Label>
                  <Textarea id="address" data-testid="checkout-address" value={form.address} onChange={update("address")} rows={4} placeholder={t("address_placeholder")} />
                </div>
              </div>
              <Button
                onClick={submit}
                disabled={busy}
                data-testid="pay-now-btn"
                className="mt-8 w-full h-12 text-base rounded-full text-white"
                style={{ backgroundColor: "var(--forest)" }}
              >
                {busy ? "…" : (
                  <span className="inline-flex items-center">
                    {t("place_order")} <ArrowRight className="ml-2 w-4 h-4" />
                  </span>
                )}
              </Button>
            </div>

            <aside className="bg-white rounded-2xl border border-black/5 p-6 h-fit sticky top-24">
              <h2 className="font-serif text-2xl text-forest mb-4 font-bold">{t("your_basket")}</h2>
              <ul className="divide-y divide-black/5" data-testid="cart-items">
                {items.map((i) => (
                  <li key={i.product_id} className="py-3 flex items-start gap-3">
                    <div className="flex-1">
                      <div className="text-charcoal font-medium">{localName(i)}</div>
                      <div className="text-xs text-charcoal/60 font-mono mt-0.5">
                        {i.quantity_value} {localUom(i.uom)} · ₹{i.unit_price}/{localUom(i.uom)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-forest font-semibold">₹{Number(i.line_total).toFixed(2)}</div>
                      <button
                        onClick={() => remove(i.product_id)}
                        className="mt-1 text-xs text-charcoal/40 hover:text-terracotta inline-flex items-center gap-1"
                        data-testid={`remove-item-${i.product_id}`}
                      >
                        <Trash2 className="w-3 h-3" /> {t("remove")}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-4 border-t border-black/10 flex justify-between items-baseline">
                <span className="text-charcoal/70">{t("total")}</span>
                <span className="font-mono text-2xl text-forest font-semibold" data-testid="cart-total">
                  ₹{total.toFixed(2)}
                </span>
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}
